import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const ChevronDown = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SimpleHeader = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handlePricingClick = (e) => {
    e.preventDefault();

    // If we're on the home page, scroll to pricing section
    if (location.pathname === '/') {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // If we're on any other page, navigate to home and then scroll
      navigate('/');
      setTimeout(() => {
        const pricingSection = document.getElementById('pricing');
        if (pricingSection) {
          pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const navigationItems = {
    solutions: [
      { label: "AI Marketing", href: "/solutions/ai-marketing" },
    ],
    resources: [
      { label: "Articles", href: "/resources/articles" },
      { label: "Blogs", href: "/resources/blogs" },
    ],
  };

  const handleMouseEnter = (dropdown) => {
    setActiveDropdown(dropdown);
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  return (
    <>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-[1000]">
        <div className="max-w-[1400px] mx-auto px-8 py-4 flex items-center justify-between gap-8">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 no-underline flex-shrink-0">
            <img
              src="/dt_logo.png"
              alt="Digital Trent Logo"
              className="h-10 w-auto object-contain"
            />
            <span className="text-xl font-bold text-gray-800">Digital Trent</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2 flex-1 justify-center">
            
            {/* Solutions Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('solutions')}
              onMouseLeave={handleMouseLeave}
            >
              <button className="flex items-center gap-1.5 px-4 py-2 text-[15px] font-medium text-gray-700 bg-transparent border-none rounded-md cursor-pointer transition-all hover:bg-gray-100 hover:text-blue-600">
                <span>Solutions</span>
                <ChevronDown />
              </button>
              
              {/* Invisible bridge to prevent dropdown from closing */}
              <div className="absolute top-full left-0 right-0 h-2 bg-transparent" />
              
              {activeDropdown === 'solutions' && (
                <div className="absolute top-[calc(100%+4px)] left-1/2 -translate-x-1/2 min-w-[240px] bg-white border border-gray-200 rounded-lg p-2 shadow-lg z-[100]">
                  {navigationItems.solutions.map((item, idx) => (
                    <div key={idx}>
                      {item.submenu ? (
                        /* Nested submenu item */
                        <div className="relative group/submenu">
                          <div className="flex items-center justify-between px-3 py-2.5 rounded-md text-gray-500 text-sm font-medium transition-all hover:bg-gray-100 hover:text-blue-600 cursor-pointer">
                            <span>{item.label}</span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="9 18 15 12 9 6" />
                            </svg>
                          </div>

                          {/* Invisible bridge for submenu */}
                          <div className="absolute left-full top-0 w-2 h-full bg-transparent" />

                          {/* Submenu */}
                          <div className="hidden group-hover/submenu:block absolute left-full top-0 ml-2 min-w-[200px] bg-white border border-gray-200 rounded-lg p-2 shadow-lg">
                            {item.submenu.map((subItem, subIdx) => (
                              <Link
                                key={subIdx}
                                to={subItem.href}
                                className={`flex items-center justify-between px-3 py-2.5 rounded-md no-underline text-sm font-medium transition-all ${
                                  subItem.disabled
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-500 hover:bg-gray-100 hover:text-blue-600'
                                }`}
                                onClick={(e) => subItem.disabled && e.preventDefault()}
                              >
                                <span>{subItem.label}</span>
                                {subItem.badge && (
                                  <span className="ml-2 px-2 py-1 text-[10px] font-bold text-gray-500 bg-gray-100 border border-gray-300 rounded">
                                    {subItem.badge}
                                  </span>
                                )}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ) : (
                        /* Regular menu item */
                        <Link
                          to={item.href}
                          className="block px-3 py-2.5 rounded-md no-underline text-gray-500 text-sm font-medium transition-all hover:bg-gray-100 hover:text-blue-600"
                        >
                          {item.label}
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Resources Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter('resources')}
              onMouseLeave={handleMouseLeave}
            >
              <button className="flex items-center gap-1.5 px-4 py-2 text-[15px] font-medium text-gray-700 bg-transparent border-none rounded-md cursor-pointer transition-all hover:bg-gray-100 hover:text-blue-600">
                <span>Resources</span>
                <ChevronDown />
              </button>

              {/* Invisible bridge to prevent dropdown from closing */}
              <div className="absolute top-full left-0 right-0 h-2 bg-transparent" />

              {activeDropdown === 'resources' && (
                <div className="absolute top-[calc(100%+4px)] left-1/2 -translate-x-1/2 min-w-[220px] bg-white border border-gray-200 rounded-lg p-2 shadow-lg z-[100]">
                  {navigationItems.resources.map((item, idx) => (
                    <Link
                      key={idx}
                      to={item.href}
                      className="block px-3 py-2.5 rounded-md no-underline text-gray-500 text-sm font-medium transition-all hover:bg-gray-100 hover:text-blue-600"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* COMPANY DROPDOWN - REMOVED, using standalone links instead */}
            {/* 
            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('company')}
              onMouseLeave={handleMouseLeave}
            >
              <button className="flex items-center gap-1.5 px-4 py-2 text-[15px] font-medium text-gray-700 bg-transparent border-none rounded-md cursor-pointer transition-all hover:bg-gray-100 hover:text-blue-600">
                <span>Company</span>
                <ChevronDown />
              </button>
              
              <div className="absolute top-full left-0 right-0 h-2 bg-transparent" />
              
              {activeDropdown === 'company' && (
                <div className="absolute top-[calc(100%+4px)] left-1/2 -translate-x-1/2 min-w-[220px] bg-white border border-gray-200 rounded-lg p-2 shadow-lg z-[100]">
                  {navigationItems.company.map((item, idx) => (
                    <Link 
                      key={idx} 
                      to={item.href} 
                      className="block px-3 py-2.5 rounded-md no-underline text-gray-500 text-sm font-medium transition-all hover:bg-gray-100 hover:text-blue-600"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            */}

            {/* About Us Link */}
            <Link
              to="/company/about"
              className="flex items-center gap-1.5 px-4 py-2 text-[15px] font-medium text-gray-700 bg-transparent rounded-md no-underline transition-all hover:bg-gray-100 hover:text-blue-600"
            >
              About
            </Link>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            <Link
              to="/auth/login"
              className="px-5 py-2 text-[15px] font-medium text-gray-700 no-underline rounded-md transition-all hover:bg-gray-100"
            >
              Login
            </Link>
            <Link
              to="/auth/signup"
              className="px-5 py-2 text-[15px] font-semibold text-white bg-blue-600 no-underline rounded-md transition-all hover:bg-blue-700"
            >
              Sign Up
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button 
            className="md:hidden flex flex-col gap-1 bg-transparent border-none cursor-pointer p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="w-6 h-0.5 bg-gray-700 rounded-sm transition-all" />
            <span className="w-6 h-0.5 bg-gray-700 rounded-sm transition-all" />
            <span className="w-6 h-0.5 bg-gray-700 rounded-sm transition-all" />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[9999]">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/50 animate-[fadeIn_0.3s_ease]"
            onClick={() => setMobileMenuOpen(false)} 
          />
          
          {/* Panel */}
          <div className="absolute top-0 right-0 bottom-0 w-[85%] max-w-[380px] bg-white overflow-y-auto shadow-[-4px_0_16px_rgba(0,0,0,0.1)] animate-[slideIn_0.3s_ease]">
            
            {/* Close Button */}
            <button 
              className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-gray-100 border-none rounded-lg cursor-pointer text-2xl text-gray-700 z-10 transition-all hover:bg-gray-200"
              onClick={() => setMobileMenuOpen(false)}
            >
              ✕
            </button>

            {/* Content */}
            <div className="pt-20 px-6 pb-8">

              {/* About Link */}
              <Link
                to="/company/about"
                className="block px-3 py-4 text-base font-semibold text-gray-800 no-underline border-b border-gray-200 transition-all hover:bg-gray-50 hover:pl-4"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>

              {/* Solutions Section */}
              <div className="border-b border-gray-200 py-4">
                <div className="px-3 py-3 text-base font-semibold text-gray-800">
                  Solutions
                </div>
                {navigationItems.solutions.map((item, idx) => (
                  <div key={idx}>
                    {item.submenu ? (
                      /* Item with submenu */
                      <div>
                        <div className="pl-6 pr-3 py-3 text-gray-500 text-[15px] font-medium">
                          {item.label}
                        </div>
                        {item.submenu.map((subItem, subIdx) => (
                          <Link
                            key={subIdx}
                            to={subItem.href}
                            className={`flex items-center justify-between pl-10 pr-3 py-2.5 text-[14px] no-underline transition-all ${
                              subItem.disabled
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-blue-600 hover:pl-11'
                            }`}
                            onClick={(e) => {
                              if (subItem.disabled) {
                                e.preventDefault();
                              } else {
                                setMobileMenuOpen(false);
                              }
                            }}
                          >
                            <span>{subItem.label}</span>
                            {subItem.badge && (
                              <span className="px-2 py-1 text-[10px] font-bold text-gray-500 bg-gray-100 border border-gray-300 rounded">
                                {subItem.badge}
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      /* Regular item */
                      <Link
                        to={item.href}
                        className="block pl-6 pr-3 py-3 text-gray-500 text-[15px] no-underline transition-all hover:bg-gray-50 hover:text-blue-600 hover:pl-7"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
              </div>

              {/* Resources Section */}
              <div className="border-b border-gray-200 py-4">
                <div className="px-3 py-3 text-base font-semibold text-gray-800">
                  Resources
                </div>
                {navigationItems.resources.map((item, idx) => (
                  <Link
                    key={idx}
                    to={item.href}
                    className="block pl-6 pr-3 py-3 text-gray-500 text-[15px] no-underline transition-all hover:bg-gray-50 hover:text-blue-600 hover:pl-7"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              {/* COMPANY SECTION - COMMENTED OUT, using standalone links now */}
              {/*
              <div className="border-b border-gray-200 py-4">
                <div className="px-3 py-3 text-base font-semibold text-gray-800">
                  Company
                </div>
                {navigationItems.company.map((item, idx) => (
                  <Link 
                    key={idx} 
                    to={item.href} 
                    className="block pl-6 pr-3 py-3 text-gray-500 text-[15px] no-underline transition-all hover:bg-gray-50 hover:text-blue-600 hover:pl-7"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
              */}

              {/* Mobile Actions */}
              <div className="flex flex-col gap-3 mt-6">
                <Link
                  to="/auth/login"
                  className="px-3 py-3 text-center text-[15px] font-medium text-gray-700 no-underline border border-gray-200 rounded-lg transition-all hover:bg-gray-50 hover:border-gray-300"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/auth/signup"
                  className="px-3 py-3 text-center text-[15px] font-semibold text-white bg-blue-600 no-underline rounded-lg transition-all hover:bg-blue-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animations for mobile menu */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

export default SimpleHeader;